import { useState, useEffect, useMemo } from "react";
import {
  PieChart,
  Pie,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import {
  doc,
  collection,
  onSnapshot,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
  increment,
} from "firebase/firestore";
import { db } from "../firebase/firestore";
import { useAuth } from "../context/AuthContext";
import { askGeminiJSON } from "../utils/gemini";
import { uploadPhoto, deletePhoto } from "../firebase/storage";
import { registerForNotifications, onForegroundMessage } from "../firebase/messaging";
import "./Budget.scss";

// ── Constants ─────────────────────────────────────────────────────────────────

const RK_EMAIL = "rajeshmaydev@gmail.com";
const INDU_EMAIL = "indira02032001@gmail.com";

const USER_META = {
  [RK_EMAIL]: {
    name: "RK",
    initial: "R",
    color: "var(--clr-p, #f4a5ae)",
    chipBg: "color-mix(in srgb, var(--clr-p, #f4a5ae) 20%, transparent)",
  },
  [INDU_EMAIL]: {
    name: "Indu",
    initial: "I",
    color: "#b9a5f4",
    chipBg: "#b9a5f433",
  },
};

const CURRENCIES = [
  { code: "MYR", symbol: "RM", label: "Ringgit (RM)" },
  { code: "INR", symbol: "\u20b9", label: "Rupee (\u20b9)" },
  { code: "USD", symbol: "$", label: "Dollar ($)" },
  { code: "EUR", symbol: "\u20ac", label: "Euro (\u20ac)" },
  { code: "GBP", symbol: "\u00a3", label: "Pound (\u00a3)" },
  { code: "AUD", symbol: "A$", label: "AUD (A$)" },
];

const DEFAULT_CATEGORIES = [
  { id: "rent", name: "Rent", emoji: "\ud83c\udfe0", type: "need", budgeted: 0 },
  { id: "groceries", name: "Groceries", emoji: "\ud83d\uded2", type: "need", budgeted: 0 },
  { id: "transport", name: "Transport", emoji: "\ud83d\ude97", type: "need", budgeted: 0 },
  { id: "utilities", name: "Utilities", emoji: "\ud83d\udca1", type: "need", budgeted: 0 },
  { id: "insurance", name: "Insurance", emoji: "\ud83d\udee1\ufe0f", type: "need", budgeted: 0 },
  { id: "dining", name: "Dining Out", emoji: "\ud83c\udf7d\ufe0f", type: "want", budgeted: 0 },
  { id: "shopping", name: "Shopping", emoji: "\ud83d\udecd\ufe0f", type: "want", budgeted: 0 },
  { id: "entertainment", name: "Entertainment", emoji: "\ud83c\udfac", type: "want", budgeted: 0 },
  { id: "savings", name: "Savings", emoji: "\ud83d\udcb0", type: "need", budgeted: 0 },
  { id: "emergency", name: "Emergency", emoji: "\ud83d\udea8", type: "need", budgeted: 0 },
];

const PIE_COLORS = { need: "#66bb6a", want: "#ffa726" };

const todayStr = () => new Date().toISOString().slice(0, 10);
const currentMonth = () => new Date().toISOString().slice(0, 7);

const fmtCurrency = (amount, currency = "MYR") => {
  const cur = CURRENCIES.find((c) => c.code === currency) || CURRENCIES[0];
  return `${cur.symbol}${Number(amount).toLocaleString("en", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

const fmtDateShort = (dateStr) =>
  new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });

// ── Config doc ref ────────────────────────────────────────────────────────────

const CONFIG_REF = doc(db, "budgetProfiles", "config");

// ── Main Budget component ─────────────────────────────────────────────────────

export default function Budget() {
  const { user } = useAuth();

  const [config, setConfig] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [incomeEntries, setIncomeEntries] = useState([]);
  const [monthlyDoc, setMonthlyDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(currentMonth);
  const [showSetup, setShowSetup] = useState(false);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [showAddIncome, setShowAddIncome] = useState(false);
  const [showEditBudget, setShowEditBudget] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [savingsPool, setSavingsPool] = useState(0);
  const [goalsTotal, setGoalsTotal] = useState(0);

  // Register for push notifications
  useEffect(() => {
    registerForNotifications(user.email);
    const unsub = onForegroundMessage();
    return unsub;
  }, [user.email]);

  // Config listener
  useEffect(() => {
    const unsub = onSnapshot(CONFIG_REF, (snap) => {
      if (snap.exists()) {
        setConfig(snap.data());
      } else {
        setShowSetup(true);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  // Transactions listener (month-filtered)
  useEffect(() => {
    const q = query(
      collection(db, "budgetTransactions"),
      where("month", "==", month),
    );
    return onSnapshot(q, (snap) => {
      const txs = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.date || "").localeCompare(a.date || ""));
      setTransactions(txs);
    });
  }, [month]);

  // Income entries listener (month-filtered)
  useEffect(() => {
    const q = query(
      collection(db, "budgetIncome"),
      where("month", "==", month),
    );
    return onSnapshot(q, (snap) => {
      const entries = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.date || "").localeCompare(a.date || ""));
      setIncomeEntries(entries);
    });
  }, [month]);

  // Monthly doc listener
  useEffect(() => {
    return onSnapshot(doc(db, "budgetMonthly", month), (snap) => {
      setMonthlyDoc(snap.exists() ? snap.data() : null);
    });
  }, [month]);

  // Savings pool + goals total listeners
  useEffect(() => {
    return onSnapshot(doc(db, "budgetAutoSavings", "balance"), (snap) => {
      setSavingsPool(snap.exists() ? (snap.data().balance || 0) : 0);
    });
  }, []);

  useEffect(() => {
    return onSnapshot(
      query(collection(db, "savingsGoals"), where("status", "==", "active")),
      (snap) => {
        setGoalsTotal(snap.docs.reduce((s, d) => s + (d.data().current || 0), 0));
      },
    );
  }, []);

  // ── Derived values ──────────────────────────────────────────────────────────

  const derived = useMemo(() => {
    if (!config) return null;
    const incomes = config.incomes || {};
    const baseIncome = (incomes[RK_EMAIL] || 0) + (incomes[INDU_EMAIL] || 0);
    const extraIncome = incomeEntries.reduce((s, e) => s + (e.amount || 0), 0);
    const totalIncome = baseIncome + extraIncome;
    const categories = config.categories || [];
    const rollovers = monthlyDoc?.rollovers || {};

    const totalBudgeted = categories.reduce((s, c) => s + (c.budgeted || 0), 0);
    const savingsDeduction = Math.round(totalIncome * 0.10 * 100) / 100;
    const toBeBudgeted = totalIncome - totalBudgeted - savingsDeduction;

    // Spending per category
    const spentByCategory = {};
    const spentByPerson = { [RK_EMAIL]: 0, [INDU_EMAIL]: 0 };
    let totalSpent = 0;
    let needsSpent = 0;
    let wantsSpent = 0;

    transactions.forEach((t) => {
      spentByCategory[t.categoryId] =
        (spentByCategory[t.categoryId] || 0) + t.amount;
      spentByPerson[t.spentBy] = (spentByPerson[t.spentBy] || 0) + t.amount;
      totalSpent += t.amount;
      if (t.type === "need") needsSpent += t.amount;
      else wantsSpent += t.amount;
    });

    const envelopes = categories.map((cat) => {
      const spent = spentByCategory[cat.id] || 0;
      const rollover = rollovers[cat.id] || 0;
      const available = cat.budgeted + rollover - spent;
      return { ...cat, spent, rollover, available };
    });

    const totalSaved = savingsPool + goalsTotal;
    const savingsRate =
      totalIncome > 0
        ? Math.round((totalSaved / totalIncome) * 100)
        : 0;

    return {
      baseIncome,
      extraIncome,
      totalIncome,
      totalBudgeted,
      toBeBudgeted,
      totalSpent,
      needsSpent,
      wantsSpent,
      spentByPerson,
      envelopes,
      savingsRate,
    };
  }, [config, transactions, incomeEntries, monthlyDoc, savingsPool, goalsTotal]);

  // ── Month navigation ────────────────────────────────────────────────────────

  const shiftMonth = (dir) => {
    const [y, m] = month.split("-").map(Number);
    const d = new Date(y, m - 1 + dir, 1);
    const newMonth = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    setMonth(newMonth);
  };

  const monthLabel = (() => {
    const [y, m] = month.split("-").map(Number);
    return new Date(y, m - 1).toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });
  })();

  // ── Delete transaction ──────────────────────────────────────────────────────

  const [deleting, setDeleting] = useState(false);
  const confirmDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    await deleteDoc(doc(db, "budgetTransactions", deleteTarget.id));
    setDeleteTarget(null);
    setDeleting(false);
  };

  if (loading) {
    return (
      <div className="budget">
        <div className="budget-loading">Loading budget data...</div>
      </div>
    );
  }

  const currency = config?.currency || "MYR";

  return (
    <div className="budget">
      {/* Setup modal */}
      {showSetup && (
        <SetupModal
          onDone={() => setShowSetup(false)}
        />
      )}

      {/* Edit budget modal */}
      {showEditBudget && config && (
        <EditBudgetModal
          config={config}
          transactions={transactions}
          onClose={() => setShowEditBudget(false)}
        />
      )}

      {/* Add income modal */}
      {showAddIncome && config && (
        <AddIncomeModal
          currency={config.currency || "MYR"}
          userEmail={user.email}
          onClose={() => setShowAddIncome(false)}
        />
      )}

      {/* Add expense modal */}
      {showAddExpense && config && (
        <AddExpenseModal
          config={config}
          userEmail={user.email}
          month={month}
          onClose={() => setShowAddExpense(false)}
        />
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <div className="budget-overlay">
          <div className="budget-modal budget-modal--sm">
            <p className="budget-confirm-text">
              Delete this transaction?
              <br />
              <span className="budget-confirm-sub">
                {deleteTarget.description} &mdash;{" "}
                {fmtCurrency(deleteTarget.amount, currency)}
              </span>
            </p>
            <div className="budget-modal-actions">
              <button
                className="btn-budget-cancel"
                onClick={() => setDeleteTarget(null)}
              >
                Cancel
              </button>
              <button className="btn-budget-delete" onClick={confirmDelete} disabled={deleting}>
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="budget-header">
        <div>
          <h2 className="budget-title">Our Budget</h2>
          <p className="budget-subtitle">
            Together, every ringgit has a plan
          </p>
        </div>
        <div className="budget-header-btns">
          <button
            className="btn-add-income"
            onClick={() => setShowAddIncome(true)}
            disabled={!config}
          >
            + Income
          </button>
          <button
            className="btn-add-expense"
            onClick={() => setShowAddExpense(true)}
            disabled={!config}
          >
            + Expense
          </button>
        </div>
      </div>

      {/* Month nav */}
      <div className="budget-month-nav">
        <button className="budget-month-arrow" onClick={() => shiftMonth(-1)}>
          &lsaquo;
        </button>
        <span className="budget-month-label">{monthLabel}</span>
        <button className="budget-month-arrow" onClick={() => shiftMonth(1)}>
          &rsaquo;
        </button>
      </div>

      {derived && (
        <>
          {/* Overview cards */}
          <BudgetOverview
            derived={derived}
            currency={currency}
          />

          {/* Pie chart */}
          {derived.totalSpent > 0 && (
            <NeedsWantsPie
              needsSpent={derived.needsSpent}
              wantsSpent={derived.wantsSpent}
              spentByPerson={derived.spentByPerson}
              currency={currency}
            />
          )}

          {/* Category spend chart */}
          {derived.envelopes.length > 0 && (
            <CategorySpendChart
              envelopes={derived.envelopes}
              transactions={transactions}
              currency={currency}
            />
          )}

          {/* Envelopes */}
          <div className="budget-section-card">
            <div className="budget-section-header">
              <h3 className="budget-section-title">Envelopes</h3>
              <button
                className="btn-budget-edit"
                onClick={() => setShowEditBudget(true)}
              >
                Edit
              </button>
            </div>
            <div className="budget-envelopes">
              {derived.envelopes.map((env) => (
                <EnvelopeRow key={env.id} env={env} currency={currency} />
              ))}
            </div>
          </div>

          {/* Savings Goals */}
          <SavingsGoalsSection
            baseIncome={derived.baseIncome}
            month={month}
            currency={currency}
            userEmail={user.email}
          />

          {/* Transactions + Extra Income side by side */}
          <div className="budget-tx-income-row">
            <TransactionList
              transactions={transactions}
              categories={config?.categories || []}
              currency={currency}
              userEmail={user.email}
              onDelete={setDeleteTarget}
            />
            {incomeEntries.length > 0 && (
              <IncomeList
                entries={incomeEntries}
                currency={currency}
                userEmail={user.email}
              />
            )}
          </div>

          {/* AI Insight */}
          <AiInsightCard
            config={config}
            derived={derived}
            transactions={transactions}
            month={month}
            monthlyDoc={monthlyDoc}
            currency={currency}
          />

          {/* Daily AI Review */}
          <DailyReviewCard
            transactions={transactions}
            categories={config?.categories || []}
            currency={currency}
            month={month}
            userEmail={user.email}
            totalIncome={derived.totalIncome}
          />
        </>
      )}
    </div>
  );
}

// ── Setup Modal ───────────────────────────────────────────────────────────────

function SetupModal({ onDone }) {
  const [currency, setCurrency] = useState("MYR");
  const [rkIncome, setRkIncome] = useState("");
  const [induIncome, setInduIncome] = useState("");
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);

  const totalIncome =
    (parseFloat(rkIncome) || 0) + (parseFloat(induIncome) || 0);
  const totalBudgeted = categories.reduce((s, c) => s + (c.budgeted || 0), 0);
  const savingsDeduction = Math.round(totalIncome * 0.10 * 100) / 100;
  const toBeBudgeted = totalIncome - totalBudgeted - savingsDeduction;

  const aiSuggest = async () => {
    if (totalIncome <= 0) return;
    setAiLoading(true);
    try {
      const result = await askGeminiJSON(
        `You are a budgeting advisor. Given a combined monthly income of ${totalIncome} ${currency}, suggest budget amounts for these categories using the 50/30/20 rule (50% needs, 30% wants, 20% savings/emergency). Categories: ${JSON.stringify(categories.map((c) => ({ id: c.id, name: c.name, type: c.type })))}. Respond with a JSON object mapping category id to suggested budgeted amount (number). Example: {"rent": 1500, "groceries": 800}`,
      );
      setCategories((prev) =>
        prev.map((c) => ({
          ...c,
          budgeted: result[c.id] != null ? Number(result[c.id]) : c.budgeted,
        })),
      );
    } catch {
      // silently fail — user can still set manually
    }
    setAiLoading(false);
  };

  const save = async () => {
    setSaving(true);
    await setDoc(CONFIG_REF, {
      currency,
      incomes: {
        [RK_EMAIL]: parseFloat(rkIncome) || 0,
        [INDU_EMAIL]: parseFloat(induIncome) || 0,
      },
      categories,
      aiCache: {},
    });
    setSaving(false);
    onDone();
  };

  return (
    <div className="budget-overlay">
      <div className="budget-modal budget-modal--lg">
        <div className="budget-modal-header">
          <h3>Let's set up your budget together</h3>
          <p>Enter your incomes and we'll help you plan every ringgit.</p>
        </div>
        <div className="budget-modal-body">
          <label className="budget-label">
            Currency
            <select
              className="budget-select"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
            >
              {CURRENCIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
          <div className="budget-income-row">
            <label className="budget-label">
              RK's monthly income
              <input
                className="budget-input"
                type="number"
                placeholder="e.g. 5000"
                value={rkIncome}
                onChange={(e) => setRkIncome(e.target.value)}
              />
            </label>
            <label className="budget-label">
              Indu's monthly income
              <input
                className="budget-input"
                type="number"
                placeholder="e.g. 4500"
                value={induIncome}
                onChange={(e) => setInduIncome(e.target.value)}
              />
            </label>
          </div>

          {totalIncome > 0 && (
            <div className="budget-tbb-bar">
              <span>Combined: {fmtCurrency(totalIncome, currency)}</span>
              <span
                className={`budget-tbb ${toBeBudgeted === 0 ? "budget-tbb--ok" : toBeBudgeted > 0 ? "budget-tbb--pos" : "budget-tbb--neg"}`}
              >
                To assign: {fmtCurrency(toBeBudgeted, currency)}
              </span>
            </div>
          )}

          <div className="budget-cat-header-row">
            <h4 className="budget-cat-title">Categories</h4>
            <button
              className="btn-budget-ai"
              onClick={aiSuggest}
              disabled={aiLoading || totalIncome <= 0}
            >
              {aiLoading ? "Thinking..." : "AI Suggest"}
            </button>
          </div>

          <div className="budget-cat-list">
            {categories.map((cat, i) => (
              <div key={cat.id} className="budget-cat-row">
                <span className="budget-cat-emoji">{cat.emoji}</span>
                <span className="budget-cat-name">{cat.name}</span>
                <span
                  className={`budget-type-chip budget-type-chip--${cat.type}`}
                >
                  {cat.type}
                </span>
                <input
                  className="budget-input budget-input--sm"
                  type="number"
                  value={cat.budgeted || ""}
                  placeholder="0"
                  onChange={(e) => {
                    const val = parseFloat(e.target.value) || 0;
                    setCategories((prev) =>
                      prev.map((c, j) =>
                        j === i ? { ...c, budgeted: val } : c,
                      ),
                    );
                  }}
                />
              </div>
            ))}
          </div>
        </div>
        <div className="budget-modal-actions">
          <button
            className="btn-budget-save"
            onClick={save}
            disabled={saving || totalIncome <= 0}
          >
            {saving ? "Saving..." : "Start budgeting"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Edit Budget Modal ─────────────────────────────────────────────────────────

function EditBudgetModal({ config, transactions, onClose }) {
  const [currency, setCurrency] = useState(config.currency || "MYR");
  const [rkIncome, setRkIncome] = useState(
    config.incomes?.[RK_EMAIL]?.toString() || "",
  );
  const [induIncome, setInduIncome] = useState(
    config.incomes?.[INDU_EMAIL]?.toString() || "",
  );
  const [categories, setCategories] = useState(config.categories || []);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [newCatType, setNewCatType] = useState("want");

  const totalIncome =
    (parseFloat(rkIncome) || 0) + (parseFloat(induIncome) || 0);
  const totalBudgeted = categories.reduce((s, c) => s + (c.budgeted || 0), 0);
  const savingsDeduction = Math.round(totalIncome * 0.10 * 100) / 100;
  const toBeBudgeted = totalIncome - totalBudgeted - savingsDeduction;

  const aiSuggest = async () => {
    if (totalIncome <= 0) return;
    setAiLoading(true);
    try {
      const result = await askGeminiJSON(
        `You are a budgeting advisor. Given a combined monthly income of ${totalIncome} ${currency}, suggest budget amounts for these categories using the 50/30/20 rule (50% needs, 30% wants, 20% savings/emergency). Categories: ${JSON.stringify(categories.map((c) => ({ id: c.id, name: c.name, type: c.type })))}. Respond with a JSON object mapping category id to suggested budgeted amount (number). Example: {"rent": 1500, "groceries": 800}`,
      );
      setCategories((prev) =>
        prev.map((c) => ({
          ...c,
          budgeted: result[c.id] != null ? Number(result[c.id]) : c.budgeted,
        })),
      );
    } catch {
      // silently fail
    }
    setAiLoading(false);
  };

  const addCategory = () => {
    if (!newCatName.trim()) return;
    let id = newCatName.trim().toLowerCase().replace(/\s+/g, "-");
    // Avoid duplicate IDs
    let n = 1;
    while (categories.some((c) => c.id === id)) {
      id = `${newCatName.trim().toLowerCase().replace(/\s+/g, "-")}-${n++}`;
    }
    setCategories((prev) => [
      ...prev,
      { id, name: newCatName.trim(), emoji: "\ud83d\udccc", type: newCatType, budgeted: 0 },
    ]);
    setNewCatName("");
  };

  const removeCategory = async (id) => {
    const txCount = transactions.filter((t) => t.categoryId === id).length;
    if (txCount > 0) {
      const ok = window.confirm(
        `This category has ${txCount} transaction${txCount > 1 ? "s" : ""} this month. They will be moved to "Other". Continue?`,
      );
      if (!ok) return;

      // Ensure "Other" category exists
      if (!categories.some((c) => c.id === "other")) {
        setCategories((prev) => [
          ...prev,
          { id: "other", name: "Other", emoji: "\ud83d\udccc", type: "want", budgeted: 0 },
        ]);
      }

      // Reassign orphaned transactions to "other"
      for (const tx of transactions.filter((t) => t.categoryId === id)) {
        await updateDoc(doc(db, "budgetTransactions", tx.id), {
          categoryId: "other",
        });
      }
    }
    setCategories((prev) => prev.filter((c) => c.id !== id));
  };

  const save = async () => {
    setSaving(true);
    await updateDoc(CONFIG_REF, {
      currency,
      incomes: {
        [RK_EMAIL]: parseFloat(rkIncome) || 0,
        [INDU_EMAIL]: parseFloat(induIncome) || 0,
      },
      categories,
    });
    setSaving(false);
    onClose();
  };

  return (
    <div
      className="budget-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="budget-modal budget-modal--lg">
        <div className="budget-modal-header">
          <h3>Edit Budget</h3>
          <button className="budget-modal-close" onClick={onClose}>
            &times;
          </button>
        </div>
        <div className="budget-modal-body">
          <label className="budget-label">
            Currency
            <select
              className="budget-select"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
            >
              {CURRENCIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
          <div className="budget-income-row">
            <label className="budget-label">
              RK's income
              <input
                className="budget-input"
                type="number"
                value={rkIncome}
                onChange={(e) => setRkIncome(e.target.value)}
              />
            </label>
            <label className="budget-label">
              Indu's income
              <input
                className="budget-input"
                type="number"
                value={induIncome}
                onChange={(e) => setInduIncome(e.target.value)}
              />
            </label>
          </div>

          <div className="budget-tbb-bar">
            <span>Combined: {fmtCurrency(totalIncome, currency)}</span>
            <span
              className={`budget-tbb ${toBeBudgeted === 0 ? "budget-tbb--ok" : toBeBudgeted > 0 ? "budget-tbb--pos" : "budget-tbb--neg"}`}
            >
              To assign: {fmtCurrency(toBeBudgeted, currency)}
            </span>
          </div>

          <div className="budget-cat-header-row">
            <h4 className="budget-cat-title">Categories</h4>
            <button
              className="btn-budget-ai"
              onClick={aiSuggest}
              disabled={aiLoading || totalIncome <= 0}
            >
              {aiLoading ? "Thinking..." : "AI Suggest"}
            </button>
          </div>

          <div className="budget-cat-list">
            {categories.map((cat, i) => (
              <div key={cat.id} className="budget-cat-row">
                <span className="budget-cat-emoji">{cat.emoji}</span>
                <span className="budget-cat-name">{cat.name}</span>
                <span
                  className={`budget-type-chip budget-type-chip--${cat.type}`}
                >
                  {cat.type}
                </span>
                <input
                  className="budget-input budget-input--sm"
                  type="number"
                  value={cat.budgeted || ""}
                  placeholder="0"
                  onChange={(e) => {
                    const val = parseFloat(e.target.value) || 0;
                    setCategories((prev) =>
                      prev.map((c, j) =>
                        j === i ? { ...c, budgeted: val } : c,
                      ),
                    );
                  }}
                />
                <button
                  className="btn-cat-remove"
                  onClick={() => removeCategory(cat.id)}
                  title="Remove category"
                >
                  &times;
                </button>
              </div>
            ))}
          </div>

          <div className="budget-add-cat-row">
            <input
              className="budget-input"
              placeholder="New category name"
              value={newCatName}
              onChange={(e) => setNewCatName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addCategory()}
            />
            <select
              className="budget-select budget-select--sm"
              value={newCatType}
              onChange={(e) => setNewCatType(e.target.value)}
            >
              <option value="need">Need</option>
              <option value="want">Want</option>
            </select>
            <button type="button" className="btn-budget-add-cat" onClick={addCategory} disabled={!newCatName.trim()}>
              +
            </button>
          </div>
        </div>
        <div className="budget-modal-actions">
          <button className="btn-budget-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-budget-save"
            onClick={save}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Add Expense Modal ─────────────────────────────────────────────────────────

function AddExpenseModal({ config, userEmail, month, onClose }) {
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(todayStr());
  const [categoryId, setCategoryId] = useState("");
  const [type, setType] = useState("");
  const [recurring, setRecurring] = useState("");
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggested, setAiSuggested] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const categories = config.categories || [];
  const currency = config.currency || "MYR";

  // Build suggestions from aiCache keys (past descriptions)
  const allPastDescs = Object.keys(config.aiCache || {});
  const suggestions = description.trim().length > 0
    ? allPastDescs.filter((d) =>
        d.includes(description.trim().toLowerCase()) && d !== description.trim().toLowerCase()
      ).slice(0, 5)
    : [];

  const pickSuggestion = (desc) => {
    setDescription(desc);
    setShowSuggestions(false);
    // Auto-fill category and type from cache
    const cached = config.aiCache?.[desc];
    if (cached) {
      setCategoryId(cached.categoryId);
      setType(cached.type);
      setAiSuggested(true);
    }
  };

  const categorize = async () => {
    const desc = description.trim().toLowerCase();
    if (!desc) return;

    // Check cache first
    const cached = config.aiCache?.[desc];
    if (cached) {
      setCategoryId(cached.categoryId);
      setType(cached.type);
      setAiSuggested(true);
      return;
    }

    setAiLoading(true);
    try {
      const result = await askGeminiJSON(
        `Classify this expense. Expense: "${description}".
Available categories: ${JSON.stringify(categories.map((c) => ({ id: c.id, name: c.name })))}.
If the expense fits an existing category confidently, respond with: {"categoryId": "<existing id>", "type": "need" or "want"}
If NONE of the existing categories fit well, create a new one and respond with: {"categoryId": "<new_short_id>", "type": "need" or "want", "newCategory": {"name": "<Human readable name>", "emoji": "<single relevant emoji>"}}
Respond with JSON only.`,
      );

      let targetCategoryId = result.categoryId || "";
      const resultType = result.type || "want";

      // AI created a new category — add it to config
      if (result.newCategory && targetCategoryId) {
        const alreadyExists = categories.some((c) => c.id === targetCategoryId);
        if (!alreadyExists) {
          const newCat = {
            id: targetCategoryId,
            name: result.newCategory.name || targetCategoryId,
            emoji: result.newCategory.emoji || "\ud83d\udccc",
            type: resultType,
            budgeted: 0,
          };
          await updateDoc(CONFIG_REF, {
            categories: [...categories, newCat],
          });
        }
      }

      setCategoryId(targetCategoryId);
      setType(resultType);
      setAiSuggested(true);

      // Cache the result
      await updateDoc(CONFIG_REF, {
        [`aiCache.${desc}`]: {
          categoryId: targetCategoryId,
          type: resultType,
        },
      });
    } catch {
      // AI failed, user picks manually
    }
    setAiLoading(false);
  };

  const save = async () => {
    const amt = parseFloat(amount);
    if (!amt || !description.trim() || !categoryId || !type) return;
    setSaving(true);
    const txMonth = date.slice(0, 7);
    await addDoc(collection(db, "budgetTransactions"), {
      description: description.trim(),
      amount: amt,
      categoryId,
      type,
      spentBy: userEmail,
      month: txMonth,
      date,
      recurring: recurring || null,
      createdAt: serverTimestamp(),
      aiSuggested,
    });
    setSaving(false);
    onClose();
  };

  return (
    <div
      className="budget-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="budget-modal">
        <div className="budget-modal-header">
          <h3>Add Expense</h3>
          <button className="budget-modal-close" onClick={onClose}>
            &times;
          </button>
        </div>
        <div className="budget-modal-body">
          <label className="budget-label">
            What did you spend on? *
            <div className="budget-autocomplete">
              <input
                className="budget-input"
                type="text"
                placeholder="e.g. Grab ride to work"
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  setShowSuggestions(true);
                  setAiSuggested(false);
                }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                autoFocus
              />
              {showSuggestions && suggestions.length > 0 && (
                <div className="budget-suggestions">
                  {suggestions.map((s) => (
                    <button
                      key={s}
                      type="button"
                      className="budget-suggestion-item"
                      onMouseDown={() => pickSuggestion(s)}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </label>
          <label className="budget-label">
            Amount ({CURRENCIES.find((c) => c.code === currency)?.symbol}) *
            <input
              className="budget-input"
              type="number"
              placeholder="e.g. 25"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </label>
          <label className="budget-label">
            Date *
            <input
              className="budget-input"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </label>

          <div className="budget-ai-categorize">
            <button
              className="btn-budget-ai"
              onClick={categorize}
              disabled={aiLoading || !description.trim()}
            >
              {aiLoading ? "Classifying..." : "AI Categorize"}
            </button>
            {aiSuggested && (
              <span className="budget-ai-badge">AI suggested</span>
            )}
          </div>

          <label className="budget-label">
            Category *
            <select
              className="budget-select"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
            >
              <option value="">Select category</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.emoji} {c.name}
                </option>
              ))}
            </select>
          </label>

          <div className="budget-label">
            Type *
            <div className="budget-type-toggle">
              {["need", "want"].map((t) => (
                <button
                  key={t}
                  type="button"
                  className={`budget-type-btn ${type === t ? "active" : ""} budget-type-btn--${t}`}
                  onClick={() => setType(t)}
                >
                  {t === "need" ? "Need" : "Want"}
                </button>
              ))}
            </div>
          </div>

          <label className="budget-label">
            Recurring?
            <select
              className="budget-select"
              value={recurring}
              onChange={(e) => setRecurring(e.target.value)}
            >
              <option value="">No — one-time expense</option>
              <option value="monthly">Monthly</option>
              <option value="bimonthly">Bi-monthly</option>
              <option value="annually">Annually</option>
            </select>
          </label>
        </div>
        <div className="budget-modal-actions">
          <button className="btn-budget-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-budget-save"
            onClick={save}
            disabled={
              saving ||
              !description.trim() ||
              !amount ||
              !categoryId ||
              !type
            }
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Add Income Modal ──────────────────────────────────────────────────────────

function AddIncomeModal({ currency, userEmail, onClose }) {
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(todayStr());
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const amt = parseFloat(amount);
    if (!amt || !description.trim()) return;
    setSaving(true);
    const txMonth = date.slice(0, 7);
    await addDoc(collection(db, "budgetIncome"), {
      description: description.trim(),
      amount: amt,
      addedBy: userEmail,
      month: txMonth,
      date,
      createdAt: serverTimestamp(),
    });

    // Auto-deposit 10% into savings pool
    const deposit = Math.round(amt * 0.10 * 100) / 100;
    if (deposit > 0) {
      await setDoc(doc(db, "budgetAutoSavings", "balance"), {
        balance: increment(deposit),
        totalDeposited: increment(deposit),
      }, { merge: true });
    }

    setSaving(false);
    onClose();
  };

  return (
    <div
      className="budget-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="budget-modal">
        <div className="budget-modal-header">
          <h3>Add Income</h3>
          <button className="budget-modal-close" onClick={onClose}>
            &times;
          </button>
        </div>
        <div className="budget-modal-body">
          <label className="budget-label">
            What's this income from? *
            <input
              className="budget-input"
              type="text"
              placeholder="e.g. Freelance gig, bonus, gift"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              autoFocus
            />
          </label>
          <label className="budget-label">
            Amount ({CURRENCIES.find((c) => c.code === currency)?.symbol}) *
            <input
              className="budget-input"
              type="number"
              placeholder="e.g. 500"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </label>
          <label className="budget-label">
            Date *
            <input
              className="budget-input"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </label>
        </div>
        <div className="budget-modal-actions">
          <button className="btn-budget-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-budget-save"
            onClick={save}
            disabled={saving || !description.trim() || !amount}
          >
            {saving ? "Saving..." : "Add Income"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Budget Overview cards ─────────────────────────────────────────────────────

function BudgetOverview({ derived, currency }) {
  const cards = [
    {
      label: "Income",
      value: derived.totalIncome,
      sub: derived.extraIncome > 0 ? `Base ${fmtCurrency(derived.baseIncome, currency)} + ${fmtCurrency(derived.extraIncome, currency)} extra` : null,
      cls: "",
    },
    {
      label: "Spent",
      value: derived.totalSpent,
      sub: `${Math.round((derived.totalSpent / Math.max(derived.totalIncome, 1)) * 100)}% of income`,
      cls: "",
    },
    {
      label: "To Assign",
      value: derived.toBeBudgeted,
      sub: null,
      cls:
        derived.toBeBudgeted === 0
          ? "budget-ov-card--ok"
          : derived.toBeBudgeted > 0
            ? "budget-ov-card--pos"
            : "budget-ov-card--neg",
    },
    {
      label: "Savings Rate",
      value: null,
      sub: `${derived.savingsRate}%`,
      cls: "",
    },
  ];

  return (
    <div className="budget-overview">
      {cards.map((card) => (
        <div key={card.label} className={`budget-ov-card ${card.cls}`}>
          <div className="budget-ov-label">{card.label}</div>
          {card.value != null && (
            <div className="budget-ov-value">
              {fmtCurrency(card.value, currency)}
            </div>
          )}
          {card.sub && <div className="budget-ov-sub">{card.sub}</div>}
        </div>
      ))}
    </div>
  );
}

// ── Needs vs Wants Pie ────────────────────────────────────────────────────────

function NeedsWantsPie({ needsSpent, wantsSpent, spentByPerson, currency }) {
  const data = [
    { name: "Needs", value: needsSpent },
    { name: "Wants", value: wantsSpent },
  ];

  return (
    <div className="budget-section-card">
      <h3 className="budget-section-title">Needs vs Wants</h3>
      <div className="budget-pie-wrap">
        <ResponsiveContainer width="100%" height={200}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={80}
              paddingAngle={3}
              dataKey="value"
            >
              <Cell fill={PIE_COLORS.need} />
              <Cell fill={PIE_COLORS.want} />
            </Pie>
            <Tooltip
              formatter={(value) => fmtCurrency(value, currency)}
              contentStyle={{
                fontFamily: "Lato",
                fontSize: 12,
                borderRadius: 10,
                border: "1px solid #e8c4d6",
                background: "#fffbf7",
              }}
            />
            <Legend
              wrapperStyle={{ fontFamily: "Lato", fontSize: 12 }}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="budget-pie-person">
          {[RK_EMAIL, INDU_EMAIL].map((email) => {
            const meta = USER_META[email];
            return (
              <span key={email} className="budget-person-chip" style={{ background: meta.chipBg, color: meta.color }}>
                {meta.initial}: {fmtCurrency(spentByPerson[email] || 0, currency)}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Category Spend Chart ──────────────────────────────────────────────────────

function CategorySpendChart({ envelopes, transactions, currency }) {
  // Compute spending excluding recurring transactions
  const nonRecurringSpent = {};
  transactions.forEach((t) => {
    if (!t.recurring) {
      nonRecurringSpent[t.categoryId] = (nonRecurringSpent[t.categoryId] || 0) + t.amount;
    }
  });

  const data = envelopes
    .map((env) => {
      const spent = nonRecurringSpent[env.id] || 0;
      return {
        name: `${env.emoji} ${env.name}`,
        budgeted: env.budgeted,
        spent,
        isOver: spent > env.budgeted,
      };
    })
    .filter((d) => d.budgeted > 0 || d.spent > 0)
    .sort((a, b) => b.spent - a.spent);
  const chartHeight = Math.max(200, data.length * 44);

  return (
    <div className="budget-section-card">
      <h3 className="budget-section-title">Spending by Category</h3>
      <p className="budget-chart-note">Excludes recurring expenses</p>
      <ResponsiveContainer width="100%" height={chartHeight}>
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 5, right: 24, left: 10, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(90,74,66,0.08)" horizontal={false} />
          <XAxis
            type="number"
            tick={{ fontFamily: "Lato", fontSize: 11, fill: "#8b7b73" }}
            tickLine={false}
            axisLine={{ stroke: "rgba(90,74,66,0.15)" }}
          />
          <YAxis
            type="category"
            dataKey="name"
            width={120}
            tick={{ fontFamily: "Lato", fontSize: 12, fill: "#5a4a42" }}
            tickLine={false}
            axisLine={false}
          />
          <Tooltip
            formatter={(value) => fmtCurrency(value, currency)}
            contentStyle={{
              fontFamily: "Lato",
              fontSize: 12,
              borderRadius: 10,
              border: "1px solid #e8c4d6",
              background: "#fffbf7",
              boxShadow: "0 4px 16px rgba(90,74,66,0.1)",
            }}
          />
          <Legend wrapperStyle={{ fontFamily: "Lato", fontSize: 12 }} />
          <Bar dataKey="budgeted" name="Budgeted" fill="#d4d4d4" radius={[0, 4, 4, 0]} barSize={14} />
          <Bar dataKey="spent" name="Spent" radius={[0, 4, 4, 0]} barSize={14}>
            {data.map((entry, i) => (
              <Cell key={i} fill={entry.isOver ? "#e57373" : "#66bb6a"} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Envelope Row ──────────────────────────────────────────────────────────────

function EnvelopeRow({ env, currency }) {
  const isOver = env.spent > env.budgeted;
  // Use the larger of budgeted or spent as the 100% reference
  const maxVal = Math.max(env.budgeted, env.spent, 1);
  const budgetedPct = Math.round((env.budgeted / maxVal) * 100);
  const spentPct = Math.round((env.spent / maxVal) * 100);

  return (
    <div className="budget-envelope">
      <div className="budget-env-top">
        <span className="budget-env-emoji">{env.emoji}</span>
        <span className="budget-env-name">{env.name}</span>
        <span className={`budget-type-chip budget-type-chip--${env.type}`}>
          {env.type}
        </span>
        <span className={`budget-env-avail ${isOver ? "budget-env-avail--over" : ""}`}>
          {fmtCurrency(env.available, currency)}
        </span>
      </div>
      <div className="budget-env-bar-wrap">
        {/* Yellow = budgeted */}
        <div
          className="budget-env-bar budget-env-bar--budgeted"
          style={{ width: `${budgetedPct}%` }}
        />
        {/* Green = spent (up to budget amount) */}
        <div
          className="budget-env-bar budget-env-bar--spent"
          style={{ width: `${isOver ? budgetedPct : spentPct}%` }}
        />
        {/* Red + blink = overspend portion */}
        {isOver && (
          <div
            className="budget-env-bar budget-env-bar--over"
            style={{ left: `${budgetedPct}%`, width: `${spentPct - budgetedPct}%` }}
          />
        )}
      </div>
      <div className="budget-env-bottom">
        <span>
          {fmtCurrency(env.spent, currency)} / {fmtCurrency(env.budgeted, currency)}
        </span>
        {env.rollover !== 0 && (
          <span className="budget-env-rollover">
            {env.rollover > 0 ? "+" : ""}
            {fmtCurrency(env.rollover, currency)} rollover
          </span>
        )}
      </div>
    </div>
  );
}

// ── Transaction List ──────────────────────────────────────────────────────────

function TransactionList({
  transactions,
  categories,
  currency,
  userEmail,
  onDelete,
}) {
  if (transactions.length === 0) {
    return (
      <div className="budget-section-card budget-empty">
        No transactions this month yet. Tap "+ Expense" to add one.
      </div>
    );
  }

  const catMap = {};
  categories.forEach((c) => {
    catMap[c.id] = c;
  });

  return (
    <div className="budget-section-card">
      <h3 className="budget-section-title">Transactions</h3>
      <div className="budget-tx-scroll">
        {transactions.map((tx) => {
          const cat = catMap[tx.categoryId];
          const meta = USER_META[tx.spentBy];
          const canDel = tx.spentBy === userEmail;
          return (
            <div key={tx.id} className="budget-tx-row">
              <span className="budget-tx-date">{fmtDateShort(tx.date)}</span>
              <span className="budget-tx-desc">{tx.description}</span>
              <span className="budget-tx-amount">
                {fmtCurrency(tx.amount, currency)}
              </span>
              {tx.recurring && (
                <span className="budget-recurring-chip">{tx.recurring}</span>
              )}
              {cat && (
                <span className="budget-tx-cat">
                  {cat.emoji}
                </span>
              )}
              {meta && (
                <span
                  className="budget-person-chip budget-person-chip--sm"
                  style={{ background: meta.chipBg, color: meta.color }}
                >
                  {meta.initial}
                </span>
              )}
              {canDel && (
                <button
                  className="btn-tx-delete"
                  onClick={() => onDelete(tx)}
                >
                  &times;
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── AI Insight Card ───────────────────────────────────────────────────────────

function AiInsightCard({ config, derived, transactions, month, monthlyDoc, currency }) {
  const [loading, setLoading] = useState(false);
  const [insight, setInsight] = useState(monthlyDoc?.aiInsight || "");

  useEffect(() => {
    setInsight(monthlyDoc?.aiInsight || "");
  }, [monthlyDoc?.aiInsight]);

  const generate = async () => {
    setLoading(true);
    try {
      const categories = (config.categories || []).map((c) => ({
        name: c.name,
        type: c.type,
        budgeted: c.budgeted,
        spent:
          derived.envelopes.find((e) => e.id === c.id)?.spent || 0,
      }));
      const recentTx = transactions.slice(0, 20).map((t) => ({
        description: t.description,
        amount: t.amount,
        type: t.type,
      }));

      const result = await askGeminiJSON(
        `You are a warm, encouraging budgeting advisor for a couple. Analyze this monthly budget and give 2-3 sentences of practical advice. Focus on: needs coverage, overspending on wants, and savings rate. Be supportive, not judgmental. Use ${currency} for currency. Total income: ${derived.totalIncome}. Categories: ${JSON.stringify(categories)}. Recent transactions: ${JSON.stringify(recentTx)}. Respond with JSON: {"insight": "your advice here"}`,
      );

      const text = result.insight || result;
      setInsight(text);
      await setDoc(
        doc(db, "budgetMonthly", month),
        { aiInsight: text, insightGeneratedAt: serverTimestamp() },
        { merge: true },
      );
    } catch {
      setInsight("Couldn't generate insight right now. Try again later.");
    }
    setLoading(false);
  };

  return (
    <div className="budget-section-card budget-insight-card">
      <h3 className="budget-section-title">AI Insight</h3>
      {insight ? (
        <p className="budget-insight-text">{insight}</p>
      ) : (
        <p className="budget-insight-empty">
          Tap the button below to get AI-powered advice on your spending.
        </p>
      )}
      <button
        className="btn-budget-ai"
        onClick={generate}
        disabled={loading}
      >
        {loading
          ? "Analyzing..."
          : insight
            ? "Refresh insight"
            : "How are we doing?"}
      </button>
    </div>
  );
}

// ── Daily Review Card ─────────────────────────────────────────────────────────

function DailyReviewCard({ transactions, categories, currency, month, userEmail, totalIncome }) {
  const [selectedDate, setSelectedDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [review, setReview] = useState("");

  // Build list of unique dates that have transactions this month
  const txDates = [...new Set(transactions.map((t) => t.date))].sort();

  // Auto-select the latest date with transactions
  useEffect(() => {
    if (txDates.length > 0 && !txDates.includes(selectedDate)) {
      setSelectedDate(txDates[txDates.length - 1]);
      setReview("");
    }
  }, [txDates.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  const dayTxs = transactions.filter((t) => t.date === selectedDate);

  const catMap = {};
  categories.forEach((c) => { catMap[c.id] = c; });

  const generate = async () => {
    if (dayTxs.length === 0) return;
    setLoading(true);
    try {
      const dayData = dayTxs.map((t) => ({
        description: t.description,
        amount: t.amount,
        category: catMap[t.categoryId]?.name || t.categoryId,
        type: t.type,
        spentBy: USER_META[t.spentBy]?.name || t.spentBy,
      }));
      const dayTotal = dayTxs.reduce((s, t) => s + t.amount, 0);
      const needsTotal = dayTxs.filter((t) => t.type === "need").reduce((s, t) => s + t.amount, 0);
      const wantsTotal = dayTxs.filter((t) => t.type === "want").reduce((s, t) => s + t.amount, 0);
      const dateFmt = new Date(selectedDate + "T00:00:00").toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
      });

      const monthSpentSoFar = transactions.reduce((s, t) => s + t.amount, 0);

      const result = await askGeminiJSON(
        `You are a friendly budgeting advisor for a couple (Rajesh and Indira). Review their spending for ${dateFmt}.

Monthly income: ${fmtCurrency(totalIncome, currency)}
Month spent so far: ${fmtCurrency(monthSpentSoFar, currency)}
Day total: ${fmtCurrency(dayTotal, currency)} (Needs: ${fmtCurrency(needsTotal, currency)}, Wants: ${fmtCurrency(wantsTotal, currency)})
Transactions: ${JSON.stringify(dayData)}

Give a short, specific review of this day's spending:
- Call out anything that stands out (good or bad)
- If there are unnecessary wants, gently suggest alternatives
- If it was a lean day, praise them
- Keep it to 2-3 sentences, be warm and specific (mention actual items by name)
Use ${currency} for currency.
Respond with JSON: {"review": "your review here"}`,
      );

      const text = result.review || "";
      setReview(text);

      // Save to Firestore — triggers Cloud Function for push notifications
      if (text) {
        const dateFmtShort = new Date(selectedDate + "T00:00:00").toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        });
        await setDoc(
          doc(db, "budgetMonthly", month),
          {
            dailyReview: text,
            dailyReviewDate: dateFmtShort,
            dailyReviewBy: USER_META[userEmail]?.name || "Someone",
            dailyReviewAt: serverTimestamp(),
          },
          { merge: true },
        );
      }
    } catch {
      setReview("Couldn't review this day right now. Try again later.");
    }
    setLoading(false);
  };

  return (
    <div className="budget-section-card budget-daily-card">
      <h3 className="budget-section-title">Daily Review</h3>
      <div className="budget-daily-controls">
        <select
          className="budget-select"
          value={selectedDate}
          onChange={(e) => {
            setSelectedDate(e.target.value);
            setReview("");
          }}
        >
          {txDates.length > 0 ? (
            txDates.map((d) => (
              <option key={d} value={d}>
                {new Date(d + "T00:00:00").toLocaleDateString("en-US", {
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                })}
                {" "}({transactions.filter((t) => t.date === d).length} items)
              </option>
            ))
          ) : (
            <option value={selectedDate}>No transactions yet</option>
          )}
        </select>
        <button
          className="btn-budget-ai"
          onClick={generate}
          disabled={loading || dayTxs.length === 0}
        >
          {loading ? "Reviewing..." : "Review this day"}
        </button>
      </div>

      {dayTxs.length > 0 && (
        <div className="budget-daily-summary">
          {dayTxs.map((tx) => (
            <div key={tx.id} className="budget-daily-tx">
              <span>{catMap[tx.categoryId]?.emoji || "\ud83d\udccc"}</span>
              <span className="budget-daily-tx-desc">{tx.description}</span>
              <span className={`budget-type-chip budget-type-chip--${tx.type}`}>{tx.type}</span>
              <span className="budget-daily-tx-amt">{fmtCurrency(tx.amount, currency)}</span>
            </div>
          ))}
        </div>
      )}

      {review && <p className="budget-insight-text">{review}</p>}
      {!review && dayTxs.length === 0 && (
        <p className="budget-insight-empty">No expenses on this date.</p>
      )}
    </div>
  );
}

// ── Savings Goals Section ─────────────────────────────────────────────────────

const AUTO_SAVINGS_REF = doc(db, "budgetAutoSavings", "balance");

function SavingsGoalsSection({ baseIncome, month, currency, userEmail }) {
  const [pool, setPool] = useState(null);
  const [goals, setGoals] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [allocateTarget, setAllocateTarget] = useState(null);
  const [fundTarget, setFundTarget] = useState(null);
  const [fullTarget, setFullTarget] = useState(null);
  const [returnTarget, setReturnTarget] = useState(null);
  const [deleteGoalTarget, setDeleteGoalTarget] = useState(null);

  // Pool listener
  useEffect(() => {
    return onSnapshot(AUTO_SAVINGS_REF, (snap) => {
      setPool(snap.exists() ? snap.data() : { balance: 0, lastDepositMonth: "", totalDeposited: 0 });
    });
  }, []);

  // Goals listener
  useEffect(() => {
    return onSnapshot(
      query(collection(db, "savingsGoals"), where("status", "==", "active")),
      (snap) => setGoals(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    );
  }, []);

  // One-time fix: reset auto-savings with correct 10% of base salary for April
  useEffect(() => {
    if (!pool || !baseIncome) return;
    if (pool._reset10pct) return;
    const deposit = Math.round(baseIncome * 0.10 * 100) / 100;
    setDoc(AUTO_SAVINGS_REF, {
      balance: deposit,
      totalDeposited: deposit,
      lastDepositMonth: currentMonth(),
      _reset10pct: true,
    });
  }, [pool, baseIncome]);

  // Auto-deposit 10% of base salary once per month
  useEffect(() => {
    if (!pool || !baseIncome) return;
    const thisMonth = currentMonth();
    if (month !== thisMonth) return;
    if (pool.lastDepositMonth === thisMonth) return;

    const deposit = Math.round(baseIncome * 0.10 * 100) / 100;
    if (deposit <= 0) return;

    setDoc(AUTO_SAVINGS_REF, {
      balance: increment(deposit),
      lastDepositMonth: thisMonth,
      totalDeposited: increment(deposit),
    }, { merge: true });
  }, [pool?.lastDepositMonth, month, baseIncome]);

  const poolBalance = pool?.balance || 0;

  return (
    <>
      <div className="budget-section-card">
        <div className="budget-section-header">
          <h3 className="budget-section-title">Savings Goals</h3>
          <button className="btn-budget-edit" onClick={() => setShowCreate(true)}>
            + New Goal
          </button>
        </div>

        {/* Pool card */}
        <div className="goal-pool-card">
          <div className="goal-pool-left">
            <div className="goal-pool-label">Auto-Savings Pool</div>
            <div className="goal-pool-sub">10% of all income</div>
          </div>
          <div className="goal-pool-amount">{fmtCurrency(poolBalance, currency)}</div>
        </div>

        {/* Goal cards grid */}
        {goals.length > 0 ? (
          <div className="goal-cards-grid">
            {goals.map((goal) => (
              <GoalCard
                key={goal.id}
                goal={goal}
                currency={currency}
                poolBalance={poolBalance}
                onAllocate={() => setAllocateTarget(goal)}
                onFund={() => setFundTarget(goal)}
                onFull={() => setFullTarget(goal)}
                onReturn={() => setReturnTarget(goal)}
                onDelete={() => setDeleteGoalTarget(goal)}
              />
            ))}
          </div>
        ) : (
          <p className="budget-insight-empty">
            No goals yet. Create one to start saving!
          </p>
        )}
      </div>

      {showCreate && (
        <CreateGoalModal currency={currency} userEmail={userEmail} onClose={() => setShowCreate(false)} />
      )}
      {allocateTarget && (
        <AllocateModal goal={allocateTarget} poolBalance={poolBalance} currency={currency} onClose={() => setAllocateTarget(null)} />
      )}
      {fundTarget && (
        <FundGoalModal goal={fundTarget} currency={currency} onClose={() => setFundTarget(null)} />
      )}
      {fullTarget && (
        <FullGoalModal goal={fullTarget} currency={currency} onClose={() => setFullTarget(null)} />
      )}
      {returnTarget && (
        <ReturnToPoolModal goal={returnTarget} currency={currency} onClose={() => setReturnTarget(null)} />
      )}
      {deleteGoalTarget && (
        <DeleteGoalModal goal={deleteGoalTarget} currency={currency} onClose={() => setDeleteGoalTarget(null)} />
      )}
    </>
  );
}

// ── Goal Card ─────────────────────────────────────────────────────────────────

function GoalCard({ goal, currency, poolBalance, onAllocate, onFund, onFull, onReturn, onDelete }) {
  const isFull = goal.current >= goal.target;
  const pct = Math.min(100, Math.round((goal.current / Math.max(goal.target, 1)) * 100));
  const meta = USER_META[goal.createdBy];

  return (
    <div className={`goal-card ${isFull ? "goal-card--full" : ""} ${goal.imageUrl ? "goal-card--has-img" : ""}`}>
      {goal.imageUrl && (
        <div className="goal-card__bg" style={{ backgroundImage: `url("${goal.imageUrl}")` }} />
      )}
      <div className="goal-card__header">
        <span className="goal-card__emoji">{goal.emoji || "\ud83c\udfaf"}</span>
        <span className="goal-card__name">{goal.name}</span>
        {meta && (
          <span className="budget-person-chip budget-person-chip--sm" style={{ background: meta.chipBg, color: meta.color }}>
            {meta.initial}
          </span>
        )}
        <button className="btn-tx-delete" onClick={onDelete} title="Delete goal">&times;</button>
      </div>

      <div className="goal-card__amounts">
        <span className="goal-card__current">{fmtCurrency(goal.current, currency)}</span>
        <span className="goal-card__sep">/</span>
        <span className="goal-card__target-amt">{fmtCurrency(goal.target, currency)}</span>
      </div>

      <div className="goal-card__bar-wrap">
        <div className="goal-card__bar-bg" />
        <div className="goal-card__bar-fill" style={{ width: `${pct}%` }} />
      </div>
      <div className="goal-card__pct">{pct}%</div>

      {isFull ? (
        <div className="goal-card__actions">
          <button className="btn-budget-save btn-budget-save--xs" onClick={onFull}>
            Goal reached!
          </button>
        </div>
      ) : (
        <div className="goal-card__actions">
          <button className="btn-goal-allocate" onClick={onAllocate} disabled={poolBalance <= 0}>
            Allocate
          </button>
          <button className="btn-goal-fund" onClick={onFund}>
            + Fund
          </button>
          {goal.current > 0 && (
            <button className="btn-goal-fund" onClick={onReturn}>
              Return
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Create Goal Modal ─────────────────────────────────────────────────────────

function CreateGoalModal({ currency, userEmail, onClose }) {
  const [name, setName] = useState("");
  const [target, setTarget] = useState("");
  const [emoji, setEmoji] = useState("\ud83c\udfaf");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [saving, setSaving] = useState(false);

  const handleImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const save = async () => {
    const amt = parseFloat(target);
    if (!name.trim() || !amt || amt <= 0) return;
    setSaving(true);

    let imageUrl = "";
    let imagePath = "";
    if (imageFile) {
      const result = await uploadPhoto(imageFile, "goals");
      imageUrl = result.url;
      imagePath = result.path;
    }

    await addDoc(collection(db, "savingsGoals"), {
      name: name.trim(),
      emoji: emoji || "\ud83c\udfaf",
      target: amt,
      current: 0,
      imageUrl,
      imagePath,
      createdBy: userEmail,
      createdAt: serverTimestamp(),
      status: "active",
      completedAt: null,
    });
    setSaving(false);
    onClose();
  };

  return (
    <div className="budget-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="budget-modal">
        <div className="budget-modal-header">
          <h3>Create a Goal</h3>
          <button className="budget-modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="budget-modal-body">
          <label className="budget-label">
            Emoji
            <input className="budget-input budget-input--sm" value={emoji} onChange={(e) => setEmoji(e.target.value)} maxLength={2} />
          </label>
          <label className="budget-label">
            Goal name *
            <input className="budget-input" placeholder="e.g. Japan Trip" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          </label>
          <label className="budget-label">
            Target amount ({CURRENCIES.find((c) => c.code === currency)?.symbol}) *
            <input className="budget-input" type="number" placeholder="e.g. 3000" value={target} onChange={(e) => setTarget(e.target.value)} />
          </label>
          <label className="budget-label">
            Cover image
            <input className="budget-input" type="file" accept="image/*" onChange={handleImage} />
          </label>
          {imagePreview && (
            <div className="goal-image-preview">
              <img src={imagePreview} alt="Preview" />
            </div>
          )}
        </div>
        <div className="budget-modal-actions">
          <button className="btn-budget-cancel" onClick={onClose}>Cancel</button>
          <button className="btn-budget-save" onClick={save} disabled={saving || !name.trim() || !target}>
            {saving ? "Saving..." : "Create Goal"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Allocate Modal (pool → goal) ──────────────────────────────────────────────

function AllocateModal({ goal, poolBalance, currency, onClose }) {
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);

  const remaining = goal.target - goal.current;
  const maxAmount = Math.min(poolBalance, remaining);

  const save = async () => {
    const amt = Math.min(parseFloat(amount) || 0, maxAmount);
    if (amt <= 0) return;
    setSaving(true);
    await setDoc(AUTO_SAVINGS_REF, { balance: increment(-amt) }, { merge: true });
    await updateDoc(doc(db, "savingsGoals", goal.id), { current: Math.round((goal.current + amt) * 100) / 100 });
    setSaving(false);
    onClose();
  };

  return (
    <div className="budget-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="budget-modal budget-modal--sm">
        <div className="budget-modal-header">
          <h3>{goal.emoji} Allocate to "{goal.name}"</h3>
        </div>
        <div className="budget-modal-body">
          <p className="budget-confirm-sub">
            Pool: {fmtCurrency(poolBalance, currency)} available.
            Goal needs {fmtCurrency(remaining, currency)} more.
          </p>
          <label className="budget-label">
            Amount ({CURRENCIES.find((c) => c.code === currency)?.symbol})
            <input className="budget-input" type="number" placeholder={`Max ${fmtCurrency(maxAmount, currency)}`} value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />
          </label>
        </div>
        <div className="budget-modal-actions">
          <button className="btn-budget-cancel" onClick={onClose}>Cancel</button>
          <button className="btn-budget-save" onClick={save} disabled={saving || !amount || parseFloat(amount) <= 0}>
            {saving ? "Saving..." : "Allocate"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Fund Goal Modal (manual, outside pool) ────────────────────────────────────

function FundGoalModal({ goal, currency, onClose }) {
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);

  const remaining = goal.target - goal.current;

  const save = async () => {
    const amt = Math.min(parseFloat(amount) || 0, remaining);
    if (amt <= 0) return;
    setSaving(true);
    await updateDoc(doc(db, "savingsGoals", goal.id), {
      current: Math.round((goal.current + amt) * 100) / 100,
    });
    setSaving(false);
    onClose();
  };

  return (
    <div className="budget-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="budget-modal budget-modal--sm">
        <div className="budget-modal-header">
          <h3>{goal.emoji} Fund "{goal.name}"</h3>
        </div>
        <div className="budget-modal-body">
          <p className="budget-confirm-sub">
            {fmtCurrency(remaining, currency)} remaining to fill this goal.
          </p>
          <label className="budget-label">
            Amount ({CURRENCIES.find((c) => c.code === currency)?.symbol})
            <input className="budget-input" type="number" placeholder={`Max ${fmtCurrency(remaining, currency)}`} value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />
          </label>
        </div>
        <div className="budget-modal-actions">
          <button className="btn-budget-cancel" onClick={onClose}>Cancel</button>
          <button className="btn-budget-save" onClick={save} disabled={saving || !amount || parseFloat(amount) <= 0}>
            {saving ? "Saving..." : "Add Funds"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Full Goal Modal ───────────────────────────────────────────────────────────

function FullGoalModal({ goal, currency, onClose }) {
  const [processing, setProcessing] = useState(false);

  const markCompleted = async () => {
    setProcessing(true);
    await updateDoc(doc(db, "savingsGoals", goal.id), {
      status: "completed",
      completedAt: serverTimestamp(),
    });
    setProcessing(false);
    onClose();
  };

  const returnToPool = async () => {
    setProcessing(true);
    await setDoc(AUTO_SAVINGS_REF, { balance: increment(goal.current) }, { merge: true });
    await updateDoc(doc(db, "savingsGoals", goal.id), { current: 0 });
    setProcessing(false);
    onClose();
  };

  return (
    <div className="budget-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="budget-modal budget-modal--sm">
        <p className="budget-confirm-text">
          {goal.emoji} {goal.name} — Goal reached!
          <br />
          <span className="budget-confirm-sub">
            {fmtCurrency(goal.current, currency)} saved.
          </span>
        </p>
        <div className="budget-modal-actions" style={{ flexDirection: "column", gap: "0.5rem" }}>
          <button className="btn-budget-save" onClick={markCompleted} disabled={processing} style={{ width: "100%" }}>
            Mark as completed
          </button>
          <button className="btn-budget-cancel" onClick={returnToPool} disabled={processing} style={{ width: "100%" }}>
            Return to pool
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Income List ───────────────────────────────────────────────────────────────

function IncomeList({ entries, currency, userEmail }) {
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const confirmRemove = async () => {
    if (!confirmTarget || deleting) return;
    setDeleting(true);
    await deleteDoc(doc(db, "budgetIncome", confirmTarget.id));

    // Also reverse the 10% that was auto-deposited
    const deposit = Math.round(confirmTarget.amount * 0.10 * 100) / 100;
    if (deposit > 0) {
      await setDoc(doc(db, "budgetAutoSavings", "balance"), {
        balance: increment(-deposit),
        totalDeposited: increment(-deposit),
      }, { merge: true });
    }

    setDeleting(false);
    setConfirmTarget(null);
  };

  return (
    <div className="budget-section-card">
      <h3 className="budget-section-title">Extra Income</h3>

      {confirmTarget && (
        <div className="budget-overlay">
          <div className="budget-modal budget-modal--sm">
            <p className="budget-confirm-text">
              Delete this income?
              <br />
              <span className="budget-confirm-sub">
                {confirmTarget.description} &mdash; +{fmtCurrency(confirmTarget.amount, currency)}
              </span>
            </p>
            <div className="budget-modal-actions">
              <button className="btn-budget-cancel" onClick={() => setConfirmTarget(null)}>
                Cancel
              </button>
              <button className="btn-budget-delete" onClick={confirmRemove} disabled={deleting}>
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="budget-tx-scroll">
        {entries.map((entry) => {
          const meta = USER_META[entry.addedBy];
          const canDel = entry.addedBy === userEmail;
          return (
            <div key={entry.id} className="budget-tx-row">
              <span className="budget-tx-date">{fmtDateShort(entry.date)}</span>
              <span className="budget-tx-desc">{entry.description}</span>
              <span className="budget-tx-amount budget-income-amount">
                +{fmtCurrency(entry.amount, currency)}
              </span>
              {meta && (
                <span className="budget-person-chip budget-person-chip--sm" style={{ background: meta.chipBg, color: meta.color }}>
                  {meta.initial}
                </span>
              )}
              {canDel && (
                <button
                  className="btn-tx-delete"
                  onClick={() => setConfirmTarget(entry)}
                >
                  &times;
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Return to Pool Modal ──────────────────────────────────────────────────────

function ReturnToPoolModal({ goal, currency, onClose }) {
  const [amount, setAmount] = useState("");
  const [processing, setProcessing] = useState(false);

  const maxReturn = goal.current;

  const returnFunds = async () => {
    const amt = Math.min(parseFloat(amount) || 0, maxReturn);
    if (amt <= 0) return;
    setProcessing(true);
    await setDoc(AUTO_SAVINGS_REF, { balance: increment(amt) }, { merge: true });
    await updateDoc(doc(db, "savingsGoals", goal.id), {
      current: Math.round((goal.current - amt) * 100) / 100,
    });
    setProcessing(false);
    onClose();
  };

  const returnAll = async () => {
    setProcessing(true);
    await setDoc(AUTO_SAVINGS_REF, { balance: increment(maxReturn) }, { merge: true });
    await updateDoc(doc(db, "savingsGoals", goal.id), { current: 0 });
    setProcessing(false);
    onClose();
  };

  return (
    <div className="budget-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="budget-modal budget-modal--sm">
        <div className="budget-modal-header">
          <h3>{goal.emoji} Return from "{goal.name}"</h3>
        </div>
        <div className="budget-modal-body">
          <p className="budget-confirm-sub">
            {fmtCurrency(maxReturn, currency)} currently in this goal.
          </p>
          <label className="budget-label">
            Amount to return
            <input className="budget-input" type="number" placeholder={`Max ${fmtCurrency(maxReturn, currency)}`} value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />
          </label>
        </div>
        <div className="budget-modal-actions">
          <button className="btn-budget-cancel" onClick={returnAll} disabled={processing}>
            {processing ? "..." : "Return all"}
          </button>
          <button className="btn-budget-save" onClick={returnFunds} disabled={processing || !amount || parseFloat(amount) <= 0}>
            {processing ? "..." : "Return"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Delete Goal Modal ─────────────────────────────────────────────────────────

function DeleteGoalModal({ goal, currency, onClose }) {
  const [processing, setProcessing] = useState(false);
  const hasFunds = goal.current > 0;

  const handleDelete = async () => {
    setProcessing(true);

    // Return money to pool if goal has funds
    if (hasFunds) {
      await setDoc(AUTO_SAVINGS_REF, { balance: increment(goal.current) }, { merge: true });
    }

    // Delete image from storage if exists
    if (goal.imagePath) {
      try { await deletePhoto(goal.imagePath); } catch { /* ignore */ }
    }

    // Delete the goal document
    await deleteDoc(doc(db, "savingsGoals", goal.id));

    setProcessing(false);
    onClose();
  };

  return (
    <div className="budget-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="budget-modal budget-modal--sm">
        <p className="budget-confirm-text">
          Delete "{goal.emoji} {goal.name}"?
          {hasFunds && (
            <>
              <br />
              <span className="budget-confirm-sub">
                {fmtCurrency(goal.current, currency)} will be returned to the Auto-Savings pool.
              </span>
            </>
          )}
        </p>
        <div className="budget-modal-actions">
          <button className="btn-budget-cancel" onClick={onClose}>Cancel</button>
          <button className="btn-budget-delete" onClick={handleDelete} disabled={processing}>
            {processing ? "Deleting..." : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}
